import { computed, defineAsyncComponent, markRaw, ref, unref, watch } from 'vue';
// Already part of the SheetContent bundle: a static import keeps the first render synchronous.
import PdfLightbox from '@/Components/PdfLightbox.vue';

// Omnia records keep their 3D models and krpano panoramas in standard record
// fields. This sheet-body adapter renders the shared viewers through the same
// widget contract Hera uses; the shared SheetContent stays unaware of the tenant.
const MEDIA_SHEETS = Object.freeze({ omnia_ra: 'model', omnia_drawing: 'panorama' });
// Lazy: the registry imports this adapter, and the viewer imports shared components.
const OmniaMediaViewer = markRaw(defineAsyncComponent(() => import('../Components/Sheets/OmniaMediaViewer.vue')));

const safeUrl = value => {
    if (typeof value !== 'string' || value === '') return null;
    try {
        return ['http:', 'https:'].includes(new URL(value, 'https://catalog.invalid').protocol) ? value : null;
    } catch {
        return null;
    }
};

/** Viewer sources of a record, or null when the sheet has no Omnia media. */
export function omniaMediaSources(sheetName, record, publicAudience = false) {
    const kind = MEDIA_SHEETS[sheetName];
    if (!kind || record?.id === null || record?.id === undefined) return null;
    if (kind === 'model') {
        const modelUrl = safeUrl(record.model3dUrl);
        return modelUrl ? { modelUrl } : null;
    }
    if (!record.pano && !record.panoId) return null;
    // Manifests and tiles are proxied per record by the authorized panorama routes.
    return { panoramaUrl: `${publicAudience ? '/public' : ''}/omnia/panorama/${encodeURIComponent(String(record.id))}/manifest.xml` };
}

/**
 * Reading PDF of the digitised pages of a bibliography record (`pages_pdf`, detail only, authenticated
 * readers only), or null. The download URL is absent without the original-download permission.
 */
export function omniaVolumeSource(sheetName, record, publicAudience = false) {
    if (sheetName !== 'omnia_bibliography' || publicAudience) return null;
    const pdf = record?.pages_pdf;
    const url = safeUrl(pdf?.url);
    if (!url) return null;
    return {
        url,
        downloadUrl: safeUrl(pdf.download_url),
        title: typeof pdf.title === 'string' && pdf.title !== '' ? pdf.title : null,
    };
}

export function createOmniaMediaWidget({ sheet, record, publicAudience }) {
    const sources = computed(() => omniaMediaSources(unref(sheet)?.name, unref(record), Boolean(unref(publicAudience))));
    const volume = computed(() => omniaVolumeSource(unref(sheet)?.name, unref(record), Boolean(unref(publicAudience))));
    const showModel = ref(false);
    const showVolume = ref(false);
    const closeModel = () => { showModel.value = false; };
    const closeVolume = () => { showVolume.value = false; };
    watch(() => [unref(sheet)?.name, unref(record)?.id, sources.value?.modelUrl], closeModel);
    watch(() => [unref(sheet)?.name, unref(record)?.id, Boolean(volume.value)], closeVolume);

    const modelAction = () => ({
        label: 'Apri modello 3D',
        icon: '3d',
        activate: () => { showModel.value = true; },
        viewer: showModel.value ? {
            component: OmniaMediaViewer,
            props: { modelUrl: sources.value.modelUrl, onClose: closeModel },
        } : null,
    });
    // The shared PDF viewer reacts to `visible`, so it stays mounted while the record has a volume.
    const volumeAction = () => ({
        label: 'Sfoglia il volume',
        icon: 'pages',
        activate: () => { showVolume.value = true; },
        viewer: {
            component: PdfLightbox,
            props: {
                url: volume.value.url,
                downloadUrl: volume.value.downloadUrl,
                title: volume.value.title,
                visible: showVolume.value,
                progressive: true,
                allowDownload: Boolean(volume.value.downloadUrl),
                onClose: closeVolume,
            },
        },
    });

    return {
        slot: 'scheda:before-content-actions',
        galleryPreviewAction: computed(() => {
            if (sources.value?.modelUrl) return modelAction();
            return volume.value ? volumeAction() : null;
        }),
        actionBlocks: computed(() => sources.value?.panoramaUrl ? [{
            id: 'omnia-media-viewer',
            component: OmniaMediaViewer,
            props: sources.value,
        }] : []),
    };
}

export const sheetContentWidgetFactories = [createOmniaMediaWidget];
